"use client";

import logger from "@/utils/logger";

import MainLayout from "@/components/layout/MainLayout";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, Suspense, useEffect } from "react";
import { BootstrapProvider } from "@/providers/bootstrap-provider";
import { AuthProvider } from "@/context/auth-context";
import PreferenceProviders from "@/providers/preference-providers";
import { ToastProvider } from "@/providers/ToastProvider";
import { NotificationToastProvider } from "@/components/ui/NotificationToastContainer";
import { usePathname, useSearchParams } from "next/navigation";
import EnhancedAuthGuard from "@/components/auth/AuthGuard"; // 🔥 Use your existing enhanced guard
import InactivityProvider from "@/components/auth/InactivityProvider";
import DriverAccessBlock from "@/components/auth/DriverAccessBlock"; // 🔥 NEW: Driver access control
import { PermissionWrapper } from "@/components/wrapper/PermissionWrapper";
import { TripStatusSignalRProvider } from "@/providers/TripStatusSignalRProvider";
import { DeliveryIssueToastProvider } from "@/providers/DeliveryIssueToastProvider";
import { SignalRProvider } from "@/contexts/SignalRContext";
import { GlobalBannerProvider } from "@/providers/GlobalBannerProvider";
import { TripAnimationProvider } from "@/contexts/TripAnimationContext";
import BugsnagBoundary from "@/providers/BugsnagBoundary";
import { useAuth } from "@/context/auth-context";
import { SignalRToastListener } from "@/components/notifications/SignalRToastListener";
import RouteLoadingIndicator from "@/components/navigation/RouteLoadingIndicator";
import OfflineErrorPage from "@/components/errors/OfflineErrorPage";

const DISPLAY_LOCK_KEY = "timecard_display_lock";
const DISPLAY_LOCK_HEARTBEAT_KEY = "timecard_display_lock_heartbeat";
const DISPLAY_LOCK_STALE_AFTER_MS = 2 * 60 * 1000;

function LayoutContent({
    children,
    ignoreAuth,
}: {
    children: React.ReactNode;
    ignoreAuth: boolean;
}) {
    const pathname = usePathname() ?? "";
    const searchParams = useSearchParams();
    const { isAuthenticated, isLoading } = useAuth();

    // Normalize pathname: Remove trailing slash for consistent matching
    const normalizedPath =
        pathname !== null && pathname.endsWith("/") && pathname !== "/"
            ? pathname.slice(0, -1)
            : pathname;

    // Define route access rules
    let isPublicRoute = false;
    let showHeader = false;
    let redirectTo = "/";
    let allowedUserTypes: ("internal" | "external")[] = [
        "internal",
        "external",
    ];

    logger.info("🔍 Route Analysis:", {
        originalPathname: pathname,
        normalizedPath,
    });

    // 🔥 Route protection based on your navigation API structure
    if (normalizedPath === "" || normalizedPath === "/") {
        isPublicRoute = true;
        showHeader = false;
        logger.info("✅ Root route detected");
    } else if (normalizedPath === "/externalLogin") {
        isPublicRoute = true;
        showHeader = true;
        logger.info("✅ External login route detected");
    } else if (normalizedPath === "/403") {
        isPublicRoute = true;
        showHeader = false;
        logger.info("✅ Forbidden route detected");
    } else if (
        normalizedPath === "/externalLoginDashboard" ||
        normalizedPath?.startsWith("/externalLoginDashboard/")
    ) {
        // External dashboard - only for external users
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/externalLogin";
        allowedUserTypes = ["external"];
        logger.info(
            "🔒 External dashboard route detected (external users only)"
        );
    } else if (normalizedPath?.startsWith("/auth")) {
        isPublicRoute = true;
        showHeader = false;
        logger.info("✅ Auth route detected");
    }
    // 🔥 Routes that external users CAN access (from your navigation API)
    else if (normalizedPath?.startsWith("/request/")) {
        // External users can access: /request/create, /request/scan, /request/manage
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/";
        allowedUserTypes = ["internal", "external"];
        logger.info("✅ Request route detected (both user types allowed)");
    }
    // 🔥 NEW: Profile and preferences routes - accessible to both user types
    else if (
        normalizedPath?.startsWith("/profile/") ||
        normalizedPath === "/profile" ||
        normalizedPath?.startsWith("/preferences/") ||
        normalizedPath === "/preferences"
    ) {
        // Both internal and external users can access profile and preferences
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/";
        allowedUserTypes = ["internal", "external"];
        logger.info(
            "✅ Profile/Preferences route detected (both user types allowed)"
        );
    }
    // 🔥 NEW: FAQ/User Guide routes - accessible to both user types
    else if (
        normalizedPath?.startsWith("/documentation/") ||
        normalizedPath === "/documentation" ||
        normalizedPath?.startsWith("/faq/") ||
        normalizedPath === "/faq" ||
        normalizedPath?.startsWith("/user-guide/") ||
        normalizedPath === "/user-guide"
    ) {
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/";
        allowedUserTypes = ["internal", "external"];
        logger.info(
            "✅ Documentation/FAQ/User Guide route detected (both user types allowed)"
        );
    }
    // 🔥 Routes that ONLY internal users can access (from your navigation API)
    else if (
        normalizedPath?.startsWith("/trip/") ||
        normalizedPath?.startsWith("/staff/") ||
        normalizedPath?.startsWith("/customer/") ||
        normalizedPath?.startsWith("/analytics/") ||
        normalizedPath?.startsWith("/admin/") ||
        normalizedPath?.startsWith("/documentation/") ||
        normalizedPath === "/dashboard"
    ) {
        // Internal-only routes from navigation API
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/";
        allowedUserTypes = ["internal"];
        logger.info("🔒 Internal-only route detected:", normalizedPath);
    } else {
        // All other protected routes - internal users only by default
        isPublicRoute = false;
        showHeader = false;
        redirectTo = "/";
        allowedUserTypes = ["internal"];
        logger.info(
            "🔒 Unknown protected route detected (internal users only by default)"
        );
    }

    // Log final routing decisions
    logger.info("🔍 FINAL ROUTE DECISION:", {
        originalPathname: pathname,
        normalizedPath,
        isPublicRoute,
        willUseAuthGuard: !isPublicRoute,
        allowedUserTypes,
        showHeader,
        redirectTo,
    });

    const isFluidRoute = searchParams?.get("fluid") !== "false";

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const isDisplayLocked =
            localStorage.getItem(DISPLAY_LOCK_KEY) === "true";
        if (!isDisplayLocked) {
            return;
        }

        const authStateRaw = localStorage.getItem("app_auth_state");
        let hasAuthenticatedMarker = false;
        if (authStateRaw) {
            try {
                const parsed = JSON.parse(authStateRaw);
                hasAuthenticatedMarker = parsed?.authenticated === true;
            } catch {
                hasAuthenticatedMarker = false;
            }
        }

        // Do not allow stale display lock state to redirect unauthenticated users.
        if (!hasAuthenticatedMarker) {
            try {
                localStorage.setItem(DISPLAY_LOCK_KEY, "false");
                localStorage.removeItem(DISPLAY_LOCK_HEARTBEAT_KEY);
            } catch {
                // Ignore localStorage failures in restricted contexts.
            }
            return;
        }

        const now = Date.now();
        const heartbeatRaw = localStorage.getItem(DISPLAY_LOCK_HEARTBEAT_KEY);
        const heartbeatTs = heartbeatRaw ? Number(heartbeatRaw) : Number.NaN;
        const hasFreshHeartbeat =
            Number.isFinite(heartbeatTs) &&
            now - heartbeatTs <= DISPLAY_LOCK_STALE_AFTER_MS;

        // A stale lock can persist after a display tab closes/crashes and
        // incorrectly hijack normal app navigation.
        if (!hasFreshHeartbeat) {
            try {
                localStorage.setItem(DISPLAY_LOCK_KEY, "false");
                localStorage.removeItem(DISPLAY_LOCK_HEARTBEAT_KEY);
            } catch {
                // Ignore localStorage failures in restricted contexts.
            }
            return;
        }

        // Intentional: do not hijack regular app navigation to display routes.
        // Display pages keep themselves alive, but normal routes must never be
        // force-redirected to /display/timecard.
    }, [isAuthenticated, isLoading, normalizedPath]);

    if (ignoreAuth) {
        return (
            <ToastProvider>
                <NotificationToastProvider>
                    <GlobalBannerProvider>
                        <TripStatusSignalRProvider>
                            <TripAnimationProvider>
                                <DeliveryIssueToastProvider>
                                    <SignalRToastListener />
                                    <PreferenceProviders>
                                        <DriverAccessBlock>
                                            <MainLayout
                                                showHeader={showHeader}
                                                fluid={isFluidRoute}
                                            >
                                                {children}
                                            </MainLayout>
                                        </DriverAccessBlock>
                                    </PreferenceProviders>
                                </DeliveryIssueToastProvider>
                            </TripAnimationProvider>
                        </TripStatusSignalRProvider>
                    </GlobalBannerProvider>
                </NotificationToastProvider>
            </ToastProvider>
        );
    }

    if (isPublicRoute) {
        logger.info("✅ RENDERING PUBLIC ROUTE - NO AUTH REQUIRED");
        return (
            <ToastProvider>
                <NotificationToastProvider>
                    <GlobalBannerProvider>
                        <TripStatusSignalRProvider>
                            <TripAnimationProvider>
                                <DeliveryIssueToastProvider>
                                    <SignalRToastListener />
                                    <PreferenceProviders>
                                        <DriverAccessBlock>
                                            <MainLayout
                                                showHeader={showHeader}
                                                fluid={true}
                                            >
                                                {children}
                                            </MainLayout>
                                        </DriverAccessBlock>
                                    </PreferenceProviders>
                                </DeliveryIssueToastProvider>
                            </TripAnimationProvider>
                        </TripStatusSignalRProvider>
                    </GlobalBannerProvider>
                </NotificationToastProvider>
            </ToastProvider>
        );
    } else {
        logger.info(
            "🔒 RENDERING PROTECTED ROUTE - AUTH + USER TYPE CHECK REQUIRED"
        );
        return (
            <ToastProvider>
                <NotificationToastProvider>
                    <GlobalBannerProvider>
                        <TripStatusSignalRProvider>
                            <TripAnimationProvider>
                                <DeliveryIssueToastProvider>
                                    <SignalRToastListener />
                                    <PreferenceProviders>
                                        <EnhancedAuthGuard
                                            requireProfile={true}
                                            redirectTo={redirectTo}
                                            allowedUserTypes={allowedUserTypes} // 🔥 Use your enhanced guard with user type restrictions
                                        >
                                            <PermissionWrapper>
                                                <DriverAccessBlock>
                                                    <MainLayout
                                                        fluid={isFluidRoute}
                                                    >
                                                        {children}
                                                    </MainLayout>
                                                </DriverAccessBlock>
                                            </PermissionWrapper>
                                        </EnhancedAuthGuard>
                                    </PreferenceProviders>
                                </DeliveryIssueToastProvider>
                            </TripAnimationProvider>
                        </TripStatusSignalRProvider>
                    </GlobalBannerProvider>
                </NotificationToastProvider>
            </ToastProvider>
        );
    }
}

export default function ClientLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: true, // Enable reconnection refetching for better UX
                        retry: 1,
                        staleTime: 15 * 60 * 1000, // 15 minutes stale time for better caching
                        gcTime: 30 * 60 * 1000, // 30 minutes garbage collection time
                        refetchOnMount: false, // ✅ FIX: Respect cache instead of always refetching
                        refetchInterval: false, // Disable automatic polling
                        refetchIntervalInBackground: false,
                        notifyOnChangeProps: "all", // Optimize re-renders
                    },
                },
            })
    );

    const pathname = usePathname() ?? "";
    const [isOffline, setIsOffline] = useState(false);
    const [isStandaloneRoute, setIsStandaloneRoute] = useState(
        pathname.startsWith("/display/")
    );

    useEffect(() => {
        const updateNetworkStatus = () => {
            setIsOffline(!navigator.onLine);
        };

        updateNetworkStatus();
        window.addEventListener("online", updateNetworkStatus);
        window.addEventListener("offline", updateNetworkStatus);

        return () => {
            window.removeEventListener("online", updateNetworkStatus);
            window.removeEventListener("offline", updateNetworkStatus);
        };
    }, []);

    useEffect(() => {
        // Standalone/auth-bypass mode must be route-based only.
        // Relying on body classes can leak stale display state into protected pages.
        setIsStandaloneRoute(pathname.startsWith("/display/"));
    }, [pathname]);

    const content = (
        <BootstrapProvider>
            {isOffline ? (
                <OfflineErrorPage />
            ) : (
                <>
                    <RouteLoadingIndicator />
                    <Suspense fallback={<div>Loading...</div>}>
                        <LayoutContent ignoreAuth={isStandaloneRoute}>
                            {children}
                        </LayoutContent>
                    </Suspense>
                </>
            )}
        </BootstrapProvider>
    );

    return (
        <BugsnagBoundary>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <SignalRProvider>
                        {isStandaloneRoute ? (
                            content
                        ) : (
                            <InactivityProvider>{content}</InactivityProvider>
                        )}
                    </SignalRProvider>
                </AuthProvider>
            </QueryClientProvider>
        </BugsnagBoundary>
    );
}
